import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoctrineToolsModal — one-stop admin panel for Doctrine v1.0 primitives:
 *   1. 3x Qualifier Ceiling Pricing quote
 *   2. Project auto-tier classifier
 *   3. Corporate Resolution Packet builder (with PDF download)
 */
export default function DoctrineToolsModal({ open, onClose }) {
  const [tab, setTab] = useState("quote");
  const [rent, setRent] = useState("");
  const [quote, setQuote] = useState(null);
  const [classifyForm, setClassifyForm] = useState({
    unit_size_sqft: 800, condition: "standard",
    has_debris: false, has_damage: false, vacant_days: 0,
  });
  const [classification, setClassification] = useState(null);
  const [resForm, setResForm] = useState({
    action_reference: "", action_type: "invoice",
    entity_name: "Moovin Forward LLC",
    officer_name: "Shannon Ware", cosigner_name: "Shaneka Ware",
  });
  const [resPacket, setResPacket] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const runQuote = async () => {
    setBusy(true); setError(""); setQuote(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/doctrine/quote`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monthly_rent: parseFloat(rent || "0") }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Failed."); else setQuote(d);
    } catch { setError("Network error."); }
    setBusy(false);
  };

  const runClassify = async () => {
    setBusy(true); setError(""); setClassification(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/doctrine/classify-project`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(classifyForm),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Failed."); else setClassification(d);
    } catch { setError("Network error."); }
    setBusy(false);
  };

  const buildResolution = async () => {
    if (!resForm.action_reference.trim()) { setError("Action reference required."); return; }
    setBusy(true); setError(""); setResPacket(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/doctrine/resolution`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(resForm),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Failed."); else setResPacket(d.resolution);
    } catch { setError("Network error."); }
    setBusy(false);
  };

  if (!open) return null;

  return (
    <div data-testid="doctrine-backdrop" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.78)", backdropFilter: "blur(6px)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="doctrine-modal"
           style={{ background: "linear-gradient(180deg, #0F1729 0%, #0B1220 100%)", border: "1px solid rgba(232,184,196,0.35)", borderRadius: 14, padding: "1.5rem 1.6rem", maxWidth: 640, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <p style={{ color: "#E8B8C4", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: 0 }}>DOCTRINE v1.0 · COO LOGIC</p>
            <h2 style={{ color: "#F3F4F6", fontSize: "1.35rem", fontWeight: 800, margin: "4px 0 6px" }}>📋 Doctrine Tools</h2>
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", margin: 0 }}>3x-Qualifier pricing · Auto-tiering · Corporate Resolution Packet</p>
          </div>
          <button data-testid="doctrine-close" onClick={onClose} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {[["quote","💵 Quote"],["classify","🏷️ Auto-Tier"],["resolution","🧾 Resolution"]].map(([k,l]) => (
            <button key={k} data-testid={`doctrine-tab-${k}`} onClick={() => setTab(k)}
                    style={{ background: tab === k ? "rgba(232,184,196,0.15)" : "transparent",
                             border: `1px solid ${tab === k ? "#E8B8C4" : "rgba(255,255,255,0.12)"}`,
                             color: tab === k ? "#E8B8C4" : "#ADBED1",
                             padding: "0.45rem 0.85rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>{l}</button>
          ))}
        </div>

        {error && <div style={errBox}>{error}</div>}

        {/* QUOTE */}
        {tab === "quote" && (
          <div>
            <label style={lbl}>Monthly rent for this unit</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="doctrine-rent" type="number" min="0" step="10" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="e.g. 1250" style={inp} />
              <button data-testid="doctrine-quote-run" onClick={runQuote} disabled={busy || !rent} style={btn}>{busy ? "…" : "Quote"}</button>
            </div>
            {quote && (
              <div data-testid="doctrine-quote-result" style={resultBox}>
                <div style={{ fontSize: "0.7rem", color: "#7E92A8", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>{quote.band_label.toUpperCase()}</div>
                <div style={{ fontSize: "1.05rem", color: "#ADBED1", marginBottom: 3 }}>Rent ${Number(quote.monthly_rent).toLocaleString()} · Qualifier ${Number(quote.qualifier_income_required).toLocaleString()}/mo</div>
                <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#E8B8C4" }}>
                  {quote.fee_ceiling ? `$${Number(quote.fee_ceiling).toLocaleString()}` : "Custom Quote"}
                </div>
                <div style={{ fontSize: "0.72rem", color: "#7E92A8", marginTop: 4 }}>MF turnover fee ceiling · Doctrine v{quote.doctrine_version}</div>
              </div>
            )}
          </div>
        )}

        {/* CLASSIFY */}
        {tab === "classify" && (
          <div style={{ display: "grid", gap: 10 }}>
            <div><label style={lbl}>Unit size (sqft)</label>
              <input data-testid="doctrine-sqft" type="number" value={classifyForm.unit_size_sqft} onChange={(e) => setClassifyForm(f => ({ ...f, unit_size_sqft: parseFloat(e.target.value || "0") }))} style={inp} /></div>
            <div><label style={lbl}>Condition</label>
              <select data-testid="doctrine-condition" value={classifyForm.condition} onChange={(e) => setClassifyForm(f => ({ ...f, condition: e.target.value }))} style={inp}>
                <option value="move_in_ready">Move-in ready</option>
                <option value="standard">Standard</option>
                <option value="damaged">Damaged</option>
                <option value="trashed">Trashed</option>
              </select></div>
            <div><label style={lbl}>Vacant days</label>
              <input data-testid="doctrine-vacant" type="number" value={classifyForm.vacant_days} onChange={(e) => setClassifyForm(f => ({ ...f, vacant_days: parseInt(e.target.value || "0") }))} style={inp} /></div>
            <div style={{ display: "flex", gap: 14, marginTop: 4 }}>
              <label style={{ color: "#ADBED1", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                <input data-testid="doctrine-debris" type="checkbox" checked={classifyForm.has_debris} onChange={(e) => setClassifyForm(f => ({ ...f, has_debris: e.target.checked }))} /> Debris present
              </label>
              <label style={{ color: "#ADBED1", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                <input data-testid="doctrine-damage" type="checkbox" checked={classifyForm.has_damage} onChange={(e) => setClassifyForm(f => ({ ...f, has_damage: e.target.checked }))} /> Visible damage
              </label>
            </div>
            <button data-testid="doctrine-classify-run" onClick={runClassify} disabled={busy} style={btn}>{busy ? "…" : "Classify Project"}</button>
            {classification && (
              <div data-testid="doctrine-classify-result" style={resultBox}>
                <div style={{ fontSize: "0.7rem", color: "#7E92A8", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>DISPATCH TIER</div>
                <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "#E8B8C4" }}>{classification.tier_label}</div>
                <div style={{ fontSize: "0.78rem", color: "#ADBED1", marginTop: 6 }}>Confidence: {Math.round(classification.confidence * 100)}%</div>
                <div style={{ fontSize: "0.75rem", color: "#7E92A8", marginTop: 4 }}>Reasons: {classification.reasons.join(" · ")}</div>
              </div>
            )}
          </div>
        )}

        {/* RESOLUTION */}
        {tab === "resolution" && (
          <div style={{ display: "grid", gap: 10 }}>
            <div><label style={lbl}>Action reference</label>
              <input data-testid="doctrine-action-ref" type="text" value={resForm.action_reference} onChange={(e) => setResForm(f => ({ ...f, action_reference: e.target.value }))} placeholder="e.g. inv_abc123, prop_x1y2" style={inp} /></div>
            <div><label style={lbl}>Action type</label>
              <select data-testid="doctrine-action-type" value={resForm.action_type} onChange={(e) => setResForm(f => ({ ...f, action_type: e.target.value }))} style={inp}>
                <option value="invoice">Invoice</option>
                <option value="proposal">Proposal</option>
                <option value="vendor_contract">Vendor Contract</option>
                <option value="bank_submission">Bank Submission</option>
              </select></div>
            <div><label style={lbl}>Officer</label>
              <input type="text" value={resForm.officer_name} onChange={(e) => setResForm(f => ({ ...f, officer_name: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Co-signer</label>
              <input type="text" value={resForm.cosigner_name} onChange={(e) => setResForm(f => ({ ...f, cosigner_name: e.target.value }))} style={inp} /></div>
            <button data-testid="doctrine-resolution-build" onClick={buildResolution} disabled={busy} style={btn}>{busy ? "…" : "Build Resolution Packet"}</button>
            {resPacket && (
              <div data-testid="doctrine-resolution-result" style={resultBox}>
                <div style={{ fontSize: "0.7rem", color: "#7E92A8", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>✓ RESOLUTION READY</div>
                <div style={{ fontSize: "0.85rem", color: "#F3F4F6" }}>ID: {resPacket.resolution_id}</div>
                <a data-testid="doctrine-resolution-pdf" href={`${BACKEND_URL}/api/admin/doctrine/resolution/${resPacket.resolution_id}/pdf`} target="_blank" rel="noreferrer"
                   style={{ display: "inline-block", marginTop: 10, padding: "0.55rem 0.9rem", background: "#E8B8C4", color: "#2a1a20", borderRadius: 8, fontSize: "0.82rem", fontWeight: 800, textDecoration: "none" }}>📄 Download PDF</a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const lbl = { display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, textTransform: "uppercase" };
const inp = { width: "100%", background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, padding: "0.55rem 0.75rem", color: "#F3F4F6", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" };
const btn = { background: "linear-gradient(90deg, #E8B8C4, #C4909A)", color: "#2a1a20", border: "none", borderRadius: 8, padding: "0.65rem 1rem", fontWeight: 800, fontSize: "0.85rem", cursor: "pointer" };
const errBox = { background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.82rem", marginBottom: 12 };
const resultBox = { marginTop: 12, background: "rgba(15,20,30,0.6)", border: "1px solid rgba(232,184,196,0.3)", borderRadius: 10, padding: "0.95rem 1.05rem" };
